import React from 'react';
import {Col, Container, Row} from 'reactstrap';
import classNames from 'classnames';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faChevronCircleRight} from '@fortawesome/pro-light-svg-icons/faChevronCircleRight';
import {faQuestionCircle} from '@fortawesome/pro-light-svg-icons/faQuestionCircle';
import {faTimes} from '@fortawesome/pro-light-svg-icons/faTimes';
import {faCartPlus} from '@fortawesome/pro-light-svg-icons/faCartPlus';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';

import useBalanceText from 'utils/useBalanceText';
import useBeacon from 'utils/useBeacon';

import PageMeta from 'components/PageMeta';
import Section, {SectionImage} from 'components/Elements/Section';
import Video from 'components/Video';
import CheckoutButton from 'components/CheckoutButton';
import {SalesDisclaimer} from 'components/SalesCta';
import {ImgCard} from 'components/Elements/ImgBullet';
import {TestimonialBubble} from 'components/Testimonial';
import IconList, {IconListItem, ListIcon} from 'components/Elements/IconList';
import {RecipeTileBoxWlc} from 'components/Elements/Tile';
import {ActionHourSpeakerList} from 'components/SpeakerList';
import GuaranteeBox from 'components/Guarantee';
import Footer from 'components/Elements/Footer';

// @ts-ignore
import {recipeTileWlc} from 'data/recipe.json';

export default function ({
  variant = 'default',
  noExitIntent = false,
  noFixedVideo = false,
  pageTitle = 'Join WHOLE Life Club',
  pageDescription = 'Join and get instant access to the entire collection of WHOLE Life Club recipes, registered dieticians and certified health coaches, a warm and welcoming community that wants you to succeed, and so much MORE.',
  shareUrl = 'https://wlc.foodrevolution.org/whole/',
  shareImg = 'https://cdn.foodrevolution.org/wlc/opengraph/wlc-2021-open-graph-sales-v1-1200x630.jpg',
  facebookDescription = 'No matter how much you know...it’s what you do that matters most. Join WHOLE Life Club, and take action for your health today and your WHOLE life. Registration opens twice a year – stay tuned!',
  twitterDescription = 'No matter how much you know...it’s what you do that matters most. Join WHOLE Life Club, and take action for your health today and your #WHOLELife. Registration opens twice a year – stay tuned!',
  pinterestDescription = 'No matter how much you know...it’s what you do that matters most. Join WHOLE Life Club, and take action for your health today and your WHOLE life. Registration opens twice a year – stay tuned!',
  pinterestImage = 'https://cdn.foodrevolution.org/wlc/opengraph/wlc-2021-open-graph-sales-v1-1000x1500.jpg',
  ...props
}) {
  const dollarPrice = 1;
  const price99 = 99;
  const fullPriceYearly = 497;
  const fullPriceMonthly = 49;
  const discountPriceYearly = 197;
  const discountPriceMonthly = 29;
  const discountPennies = 0.54;
  const discountDollars = 17;

  const productSku = 'WLC';
  const productName = 'WHOLE Life Club';
  const productSlug = 'wlc-fda';
  const productValue = 197;

  function SalesSection({id, headline = null, trees = false, className = null, ...props}) {
    return (
      <Section id={id} color="purple" className={className} {...props}>
        <Container>
          <Row>
            <Col>{headline && <div className="cta-headline text-center text-white">{headline}</div>}</Col>
          </Row>
          <Row className="justify-content-center">
            <Col lg="10" xl="8">
              <div className="box-shadow-black-50 rounded">
                <div className="background-black text-white rounded-top p-3 p-lg-4">
                  <h3 className="text-uppercase text-center m-0">Get Started Now!</h3>
                </div>
                <div className="background-white rounded-bottom px-4 px-lg-5 py-4">
                  <h3 className="text-center mt-0">Save Big Now with the 14-Day Plant-Powered Accelerator discount!</h3>
                  <h3 className="text-center text-green mt-0 mb-4">
                    For just <del>${fullPriceYearly}/year</del> ${discountPriceYearly} a year.
                  </h3>
                  <ListIcon>
                    <>
                      Six delicious, whole-food, <b>Plant-Powered Recipes</b> per week. Plus,{' '}
                      <b>1,500+ fabulous recipes,</b> so you never get bored
                    </>
                    <>
                      <b>Weekly Videos</b> from Ocean, Nichole, and friends to keep you inspired, delivered straight to
                      your inbox
                    </>
                    <>
                      <b>Monthly Action Hour</b> events with health revolutionaries with the cutting-edge updates you
                      need
                    </>
                    <>
                      <b>Action Checklists</b> to keep you on track
                    </>
                    <>
                      <b>Live Q&A Calls</b> so you can get your questions answered
                    </>
                    <>
                      Access to <b>Health Coaches and Dietitians</b>
                    </>
                    <>
                      Access to the entire library of <b>hundreds of videos</b>
                    </>
                    <>
                      <b>1,000+ health articles,</b> all from the most credible sources
                    </>
                    <>
                      <b className="text-purple">NEW!</b> Personalized prompts from <b>Wellness Buddy (AI)</b>, 24/7 —
                      quick answers, plate upgrades, and step-by-step habit nudges when you need them most.
                    </>
                    <>
                      <b>Annual rates never go</b> up as long as you remain a member
                    </>
                    <>
                      A <b>state-of-the-art membership site</b> that’s easy to navigate
                    </>
                    <>And much more!</>
                  </ListIcon>
                  <div className="text-center">
                    <h3 className="section-heading text-green mb-1">
                      <del>${fullPriceYearly}/year</del>
                    </h3>
                    <h3 className="mt-0 mb-3">
                      Save BIG when you join with an annual membership — now just ${discountPriceYearly}/year! (That’s
                      less than ${discountDollars}/month!)
                    </h3>
                    <p>
                      <i>This is not just a club. It’s a life raft!</i>
                    </p>
                    <CheckoutButton
                      className="offer-box-button"
                      sku={productSku}
                      price={Number(productValue).toFixed(2)}
                      slug={productSlug}
                      {...props}
                    >
                      <FontAwesomeIcon
                        className="mr-2"
                        // @ts-ignore
                        icon={faCartPlus}
                      />{' '}
                      Buy Now
                    </CheckoutButton>
                    <SalesDisclaimer />
                  </div>
                </div>
              </div>
            </Col>
          </Row>
          {trees && (
            <Row>
              <Col>
                <div className="mt-4">
                  <Row className="trees-for-future text-white d-flex align-items-center justify-content-center">
                    <Col xs="11" lg="8" className="mb-4 mb-lg-0">
                      <h6 className="section-only-heading text-center text-lg-start text-400">
                        For every new order, we make a donation to Trees for the Future, enabling them to plant another
                        organic fruit or nut tree in a low-income community.
                      </h6>
                    </Col>
                    <Col xs="11" lg="3" className="text-center">
                      <img
                        className={classNames(`logo-trees`)}
                        alt="Trees for the Future logo"
                        src="https://cdn.foodrevolution.org/global/trees-logo-white.png"
                        width="1080"
                        height="442"
                        style={{maxHeight: '100px', width: 'auto'}}
                      />
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          )}
        </Container>
      </Section>
    );
  }

  useBeacon();
  useBalanceText();
  return (
    <div className="page d-flex flex-column">
      <PageMeta title={pageTitle} description={pageDescription} url={shareUrl} ogImage={shareImg} />
      <div className="page-content">
        <Section id="whole-offer-banner" innerClass="py-4" color="purple" angle="none">
          <Container>
            <Row className="text-center text-white">
              <Col>
                <h3 className="section-only-heading">
                  Special opportunity for 14-Day Plant-Powered Accelerator participants ONLY.
                </h3>
              </Col>
            </Row>
          </Container>
        </Section>
        <Section id="whole-header" className="section-angle-first" color="black" innerClass="text-white text-center">
          <Container>
            <Row>
              <Col>
                <h2 className="section-heading">
                  Why Most People Suffer from Pain, Brain Fog, and Chronic Disease as They Age — and How to Beat Those
                  Odds
                </h2>
              </Col>
            </Row>
            <Row className="d-flex justify-content-center">
              <Col className="px-0 px-sm-3">
                <div className="px-3 px-sm-0">
                  <h5 className="section-heading text-uppercase mt-3">
                    Watch This Important Message From Ocean Robbins:
                  </h5>
                </div>
                <Video url="https://www.youtube.com/watch?v=Nm1YK25df4o" label="FDA - WLC Sales" />
              </Col>
            </Row>
            <Row className="mt-5">
              <Col>
                <h3 className="mt-0 mb-2">
                  Only <del>${fullPriceYearly}</del> ${discountPriceYearly}/year
                </h3>
                <h5 className="text-400 mt-0 mb-3">(That’s just ${discountPennies} a day)</h5>
                <h5 className="mt-0 mb-4">
                  Or <del>${fullPriceMonthly}</del> ${discountPriceMonthly}/month
                </h5>
                <CheckoutButton sku={productSku} price={Number(productValue).toFixed(2)} slug={productSlug} {...props}>
                  <FontAwesomeIcon
                    className="mr-2"
                    // @ts-ignore
                    icon={faCartPlus}
                  />{' '}
                  Buy Now
                </CheckoutButton>
                <SalesDisclaimer iconColor="white" />
                <p className="text-balance mt-4">
                  You’ll be enrolled for a special membership price of just ${discountPriceYearly} a year (that’s $
                  {discountPennies} per day). You can also choose the ${discountPriceMonthly}
                  /month option.
                </p>
              </Col>
            </Row>
          </Container>
        </Section>
        <SectionImage
          id="whole-fact-1"
          color="white"
          imgPosition="right"
          imgUrl="https://cdn.foodrevolution.org/wlc/images/1251719888.jpg"
        >
          {
            {
              default: (
                <>
                  <h2 className="section-heading">Are you ready for healthy eating to be easy (and delicious!)?</h2>
                  <p>
                    Instead of stressing about what’s for dinner, what if you had a one-stop shop for recipes you knew
                    would both taste delicious <i>and</i> do your body good?
                  </p>
                  <p>
                    Rather than missing out on important new health discoveries, what if you could rest easy knowing you
                    have the most up-to-date facts — directly from world-renowned experts with decades of experience and
                    thousands of success stories?
                  </p>
                  <p>
                    Most importantly, what if you felt <b>totally confident</b> you’re taking <i>excellent</i> care of
                    your day-to-day and long-term health?
                  </p>
                  <p>
                    Join WHOLE Life Club today and experience the freedom of effortless health. Say goodbye to
                    confusion, frustration, and guilt. Say hello to a vibrant, thriving life. With the right tools and
                    support, you can make healthy living a reality.
                  </p>
                  <p className="text-h6 text-800">Are you ready to join us?</p>
                </>
              ),
              ad: (
                <>
                  <h2 className="section-heading text-uppercase">It’s a Fact</h2>
                  <p>
                    <b>
                      The most cutting-edge and reputable scientific research shows that when we eat the right foods:
                    </b>
                  </p>
                  <ListIcon className="mb-0">
                    <>
                      It’s possible to <b>slash the risk</b> of the world’s most dangerous health conditions.
                    </>
                    <>
                      Fatigue, sickness, and brain fog often <b>become a thing of the past.</b>
                    </>
                    <>
                      <b>Mental health can steadily and measurably improve.</b>
                    </>
                    <>
                      <b>Deep sleep</b> and <b>vibrant energy can become the norm,</b> and{' '}
                      <b>unhealthy food cravings can vanish.</b>
                    </>
                    <>
                      We <b>improve the planet</b> thanks to less soil erosion and fewer pesticides, GMOs, and carbon
                      emissions.
                    </>
                  </ListIcon>
                </>
              )
            }[variant]
          }
        </SectionImage>
        <Section id="whole-testimonial-1" color="light-gray">
          <Container>
            <Row>
              <Col>
                <h2 className="section-heading text-center mb-3">What do WHOLE Life Club members have to say?</h2>
                <p className="text-balance text-center text-16 mb-5">
                  <i>
                    After joining WHOLE Life Club, many participants adopted a whole-food, plant-centered diet. Their
                    testimonials reflect their unique experiences. Your results may vary.
                  </i>
                </p>
                {[
                  'wlcKathyWShort',
                  'wlcRobinShort',
                  'wlcTina',
                  variant === 'ad' ? 'wlcHeidiAd' : 'wlcHeidi',
                  'wlcJean'
                ].map((key) => (
                  <TestimonialBubble bgColor="white" id={key} key={key} />
                ))}
              </Col>
            </Row>
          </Container>
        </Section>
        <Section id="whole-fact-2" color="white" className="text-center">
          <Container>
            <Row>
              <Col>
                <h3 className="section-heading">
                  Millions are realizing that eating more plant-based and whole foods is the #1 thing we can do for…
                </h3>
                <div className="d-flex justify-content-center text-left mb-4">
                  <ListIcon className="m-0">
                    <>
                      <h4 className="section-only-heading">health</h4>
                    </>
                    <>
                      <h4 className="section-only-heading">longevity</h4>
                    </>
                    <>
                      <h4 className="section-only-heading">
                        disease prevention <i>and</i> reversal
                      </h4>
                    </>
                    <>
                      <h4 className="section-only-heading">environmental stability</h4>
                    </>
                    <>
                      <h4 className="section-only-heading">animal welfare</h4>
                    </>
                  </ListIcon>
                </div>
                <h5 className="text-400 mt-0">
                  <i>
                    {
                      {default: <>But no matter how much you know...</>, ad: <>But no matter how much we know...</>}[
                        variant
                      ]
                    }
                  </i>
                </h5>
                <h4 className="mt-0">
                  {
                    {
                      default: (
                        <>
                          It’s <i>what you DO</i> that matters most.
                        </>
                      ),
                      ad: (
                        <>
                          It’s <i>what we DO</i> that matters most.
                        </>
                      )
                    }[variant]
                  }
                </h4>
              </Col>
            </Row>
          </Container>
        </Section>
        <Section id="whole-eating" color="light-gray">
          <Container>
            <Row>
              <Col>
                <h2 className="section-heading text-center mb-5">
                  When it comes to eating for energy, optimal health, and feeling GOOD again…
                </h2>
              </Col>
            </Row>
            <ImgCard imgColLg="4" textColLg="8" img="https://cdn.foodrevolution.org/wlc/images/1169713649.jpg">
              <h4 className="section-heading">There’s a lot to keep up with.</h4>
              <p>“How do I know if I’m getting enough protein?”</p>
              <p>“I’m eating right, but I’m still overweight. What am I doing wrong?”</p>
              <p>“Will I become iron-deficient if I go plant-based?”</p>
            </ImgCard>
            <ImgCard imgColLg="4" textColLg="8" img="https://cdn.foodrevolution.org/wlc/images/1161412655.jpg">
              <h4 className="section-heading">New information comes out every day.</h4>
              <p>“Hmmm… this article says tofu is good, but I also heard it increases estrogen.”</p>
              <p className="text-balance">“What about calcium? I’m concerned about osteoporosis and weak bones.”</p>
              <p className="text-balance">
                “Is coconut oil really bad for your heart? Or is it still better than seed oils?”
              </p>
            </ImgCard>
            <ImgCard imgColLg="4" textColLg="8" img="https://cdn.foodrevolution.org/wlc/images/861305908.jpg">
              <h4 className="section-heading">
                {
                  {
                    default: <>Your family might not be 100% on board, and you might hear things like:</>,
                    ad: <>Family might not be 100% on board and say things like:</>
                  }[variant]
                }
              </h4>
              <p>“Black bean burgers? Again?”</p>
              <p>“Can’t we just get takeout?”</p>
              <p>“C’mon, one bite won’t hurt you.”</p>
            </ImgCard>
            <ImgCard imgColLg="4" textColLg="8" img="https://cdn.foodrevolution.org/wlc/images/1212263491.jpg">
              <h4 className="section-heading">
                {
                  {
                    default: (
                      <>
                        You <i>want</i> to do better but find yourself saying...
                      </>
                    ),
                    ad: (
                      <>
                        We all <i>want</i> to do better, but...
                      </>
                    )
                  }[variant]
                }
              </h4>
              <p>“I can’t seem to kick my craving for cookies / potato chips / my nighttime bowl of ice cream.”</p>
              <p className="text-balance">“Veggies aren’t as tasty as pizza. Can I really learn to love them?”</p>
              <p className="text-balance">“How do I find time to cook when I’m already so busy?”</p>
            </ImgCard>
          </Container>
        </Section>
        <SectionImage
          id="whole-track"
          color="white"
          imgPosition="right"
          imgUrl="https://cdn.foodrevolution.org/wlc/images/woman-carrot-thinking-square.jpg"
        >
          <h2 className="section-heading">Even when you know what you “should” do...</h2>
          <h4 className="mt-0">
            {
              {
                default: <>What’s the best way to stay inspired and on track?</>,
                ad: <>How do you stay inspired and on track?</>
              }[variant]
            }
          </h4>
          <ListIcon iconColor="purple" icon={faChevronCircleRight}>
            <>Cooking plant-based meals and keeping them interesting</>
            <>Staying on top of the most critical health topics</>
            <>
              {
                {
                  default: (
                    <>
                      Eating in a way that supports your values and doesn’t contribute to climate pollution or animal
                      cruelty
                    </>
                  ),
                  ad: <>Eating in a way that doesn’t contribute to climate pollution or animal cruelty</>
                }[variant]
              }
            </>
          </ListIcon>
          <h4 className="mt-0">
            {
              {
                default: (
                  <>
                    How do you go from simply <i>wanting</i> to eat healthier to actually committing to (and{' '}
                    <i>enjoying!</i>) foods that give you a long, vibrant life?
                  </>
                ),
                ad: (
                  <>
                    Want the quickest way to go from simply <i>wanting</i> to eat healthier to actually <i>enjoying</i>{' '}
                    foods recommended by top doctors and health experts?
                  </>
                )
              }[variant]
            }
          </h4>
        </SectionImage>
        <Section id="whole-testimonial-2" color="black" innerClass="text-white">
          <Container>
            <Row>
              <Col>
                {['wlcJuanita'].map((key) => (
                  <TestimonialBubble bgColor="white" textColor="black" id={key} key={key} />
                ))}
                <p className="text-balance text-center text-small mt-5">
                  <i>
                    After joining WHOLE Life Club, many participants have adopted a whole-food, plant-centered dietary
                    pattern. Their testimonials reflect their unique experiences. Your results will vary.
                  </i>
                </p>
              </Col>
            </Row>
          </Container>
        </Section>
        <Section id="whole-recipes" color="light-gray">
          <Container>
            <Row>
              <Col>
                <h2 className="section-heading text-center mb-5">
                  Stay Deliciously on Track with Over 1,500 Plant-Based Recipes
                </h2>
              </Col>
            </Row>
            <Row>
              {recipeTileWlc
                .filter((recipeTileWlc) => recipeTileWlc.id != 'lemon-thyme-brussels-sprouts')
                .map((recipeTileWlc, i) => (
                  <Col lg="6" className="mb-4" key={`recipeTile` + i}>
                    <RecipeTileBoxWlc id={recipeTileWlc.id} key={recipeTileWlc.id} recipeTile />
                  </Col>
                ))}
            </Row>
            <Row>
              <Col>
                <h3 className="text-center">And many, many more!</h3>
              </Col>
            </Row>
          </Container>
        </Section>
        <Section id="whole-intro" color="white">
          <Container>
            <Row className="d-flex justify-content-center">
              <Col lg="10">
                <h4 className="section-heading text-center">Introducing...</h4>
                <h2 className="section-heading text-center">WHOLE Life Club</h2>
                <h5 className="mt-0 text-400 text-center">
                  <i>
                    {
                      {
                        default: <>Motivation & Inspiration for Your Healthiest Life</>,
                        ad: <>Motivation & Inspiration for the Healthiest Life</>
                      }[variant]
                    }
                  </i>
                </h5>
              </Col>
            </Row>
            <Row>
              <Col>
                <img
                  src="https://cdn.foodrevolution.org/wlc/wlc-product-image-v20220329.png"
                  width="1200"
                  height="500"
                  alt="WHOLE Life Club graphic"
                />
                <h2 className="text-400 text-center mt-5">
                  Goodbye, bad habits. <br />
                  <b>Hello, good ones.</b>
                </h2>
                <h2 className="text-400 text-center mt-0">
                  Goodbye, confusion. <br />
                  <b>Hello, clarity.</b>
                </h2>
                <h2 className="text-400 text-center mt-0">
                  Goodbye, swimming upstream. <br />
                  <b>Hello, wind at your back.</b>
                </h2>
              </Col>
            </Row>
          </Container>
        </Section>
        <Section id="whole-more" color="green" innerClass="text-white">
          <Container>
            <Row className="d-flex justify-content-center">
              <Col lg="10">
                <h2 className="section-only-heading text-center">
                  WHOLE Life Club is <i>so much more</i> than just food
                </h2>
              </Col>
            </Row>
          </Container>
        </Section>
        <Section id="whole-more-2" color="white">
          <Container>
            <Row>
              <Col>
                <h2 className="section-heading text-uppercase text-center mb-3">
                  Weekly Updates on What Actually Works When it Comes to Eating for Optimal Health
                </h2>
                <h4 className="mt-0 text-center">
                  Join Ocean Robbins, Nichole Dandrea-Russert, MS, RDN, and Friends for Insights + Inspiration
                </h4>
              </Col>
            </Row>
            <Row className="d-flex align-items-center justify-content-center">
              <Col sm="10" lg="8" className="mb-4">
                <img
                  className="img-border rounded"
                  src="https://cdn.foodrevolution.org/wlc/images/wlc-image-sales-page-ocean-nichole-aow.jpg"
                  alt=""
                  width={1280}
                  height={720}
                />
              </Col>
            </Row>
            <Row className="d-flex justify-content-center">
              <Col lg="10">
                <p>
                  No one is born with an instruction manual on how to eat and what to do to stay healthy, but when you
                  join WHOLE Life Club, you can be sure you won’t miss out on any life-changing guidance or protocols.
                </p>
                <p>
                  Our Weekly Guides are like getting a private message from Ocean, Nichole, and friends each week. You
                  can tune in at your convenience to listen as they share the truth on MUST-KNOW topics that can
                  transform your life.
                </p>
                <p>
                  You’ll get the facts about healthy versus unhealthy fats, immune-supporting herbs, adaptogens, fitness
                  myths, cancer-kicking foods, which vitamins and minerals you really need, practical tips on breaking
                  bad habits and staying positive, and so much more. And it’s all delivered with action steps, which
                  makes it super practical and keeps you moving forward.
                </p>
                <p>With our team by your side, you’ll stay motivated to keep taking action to be your best YOU.</p>
                <TestimonialBubble id="wlcGenevieve" className="mt-5" />
              </Col>
            </Row>
          </Container>
        </Section>

        <Section id="whole-more-2" color="light-gray">
          <Container>
            <Row>
              <Col>
                <h2 className="section-heading text-uppercase text-center">
                  1,500+ PLANT-BASED RECIPES So Eating Healthy Never Gets Boring
                </h2>
              </Col>
            </Row>
            <Row className="d-flex align-items-center justify-content-center">
              <Col sm="10" lg="8" className="mb-4">
                <img src="https://cdn.foodrevolution.org/wlc/images/recipe.png" alt="" width={1000} height={779} />
              </Col>
            </Row>
            <Row className="d-flex justify-content-center">
              <Col lg="10">
                {
                  {
                    default: (
                      <>
                        <p>
                          No more staying stuck in food ruts with the same old meals over and over again. Each week,
                          you’ll get curated recipes that dovetail with your weekly guidance.
                        </p>
                        <p>
                          Discover superstars of nutrition with exciting recipes that showcase new ways to delight your
                          taste buds (and your family’s, too)!
                        </p>
                        <p>
                          For example, when you learn about zinc and how it has been found to shorten colds, you’ll get
                          zinc-rich recipes like Ginger Miso Dressing, Creamy Lentil Spinach Dahl, and Chocolate Almond
                          Mousse.
                        </p>
                        <p>
                          When the topic is avoiding anemia, you’ll love adding more iron to your plate with recipes
                          like Orange Miso-Glazed Tofu and Chocolate Raspberry Smoothie.
                        </p>
                        <p>
                          Each recipe is a super-healthy crowd-pleaser — always vegan, with gluten-free / soy-free /
                          salt-free options so everyone can partake. Bonus: each recipe is mega-delicious, too, so no
                          one will complain about eating healthy!
                        </p>
                      </>
                    ),
                    ad: (
                      <>
                        <p>
                          No more staying stuck in food ruts with the same old meals over and over again. Get curated
                          recipes each week that dovetail with Ocean’s and Nichole’s weekly guidance.
                        </p>
                        <p>
                          Discover superstars of nutrition with exciting recipes that showcase new ways to delight taste
                          buds (and the family, too)!
                        </p>
                        <p>
                          For example, when we learn about zinc and how it has been found to shorten colds, that week’s
                          recipes will be zinc-rich classics like Ginger Miso Dressing, Creamy Lentil Spinach Dahl, and
                          Chocolate Almond Mousse.
                        </p>
                        <p>
                          When the topic is avoiding anemia, enjoy adding more iron with recipes like Orange Miso-Glazed
                          Tofu and Chocolate Raspberry Smoothie.
                        </p>
                        <p>
                          Each recipe is a super-healthy crowd-pleaser — always vegan with gluten-free / soy-free /
                          salt-free options — and mega-delicious so that everyone can partake. Bonus: each recipe is
                          mega-delicious, too, making it yummy to eat healthy!
                        </p>
                      </>
                    )
                  }[variant]
                }
                <p>
                  {
                    {
                      default: (
                        <>
                          The instant you join, you’ll have the entire collection of WHOLE Life Club recipes at your
                          fingertips.
                        </>
                      ),
                      ad: (
                        <>
                          Get the entire collection of over 1,500 WHOLE Life Club recipes instantly when you join WHOLE
                          Life Club.
                        </>
                      )
                    }[variant]
                  }
                </p>
                <TestimonialBubble bgColor="white" id="wlcLydia" className="mt-5" />
              </Col>
            </Row>
          </Container>
        </Section>
        <Section id="whole-more-3" color="white">
          <Container>
            <Row>
              <Col>
                <h2 className="section-heading text-uppercase text-center">
                  {
                    {
                      default: (
                        <>
                          Monthly Action Hours with Leading Experts So You’re Always in the Know About Cutting-edge
                          Health News
                        </>
                      ),
                      ad: <>Monthly Action Hours with Leading Experts</>
                    }[variant]
                  }
                </h2>
              </Col>
            </Row>
            <Row className="d-flex justify-content-center">
              <Col lg="10">
                <p>
                  {variant === 'ad' ? (
                    <>
                      Get front-row seats to the latest science from doctors, <i>New York Times</i> best-selling
                      authors, and world-famous health experts.
                    </>
                  ) : (
                    <>
                      Would you like front-row seats to the latest science from doctors, <i>New York Times</i>{' '}
                      best-selling authors, and world-famous health experts?
                    </>
                  )}
                </p>
                <p>
                  We’re talking about training from true masters in their fields with serious credentials behind their
                  names and the success stories to back it up — not lifestyle bloggers, uncertified influencers, or
                  marketers trying to sell you something.
                </p>
                <p>
                  <b>
                    {
                      {
                        default: (
                          <>
                            As a WHOLE Life Club member, you’ll be treated to a monthly Action Hours. Plus, you’ll get
                            instant access to our entire library with health heroes like:
                          </>
                        ),
                        ad: (
                          <>
                            As a WHOLE Life Club member, you’ll be treated to a monthly Action Hours. Plus, you’ll get
                            instant access to our entire library with health heroes like:
                          </>
                        )
                      }[variant]
                    }
                  </b>
                </p>
                <Row className="justify-content-center my-4">
                  <ActionHourSpeakerList />
                </Row>
                <h4 className="text-center mt-0">Get the scoop on:</h4>
                <div className="d-flex justify-content-center mb-4">
                  <ListIcon className="m-0">
                    <>How to eat plant-based on a budget</>
                    <>The secret to longer lifespans</>
                    <>{{default: <>Eating to defeat cancer</>, ad: <>Eating to defeat free radicals</>}[variant]}</>
                    <>Ending food addiction</>
                    <>Conquering autoimmune disease</>
                    <>Healthy hormones</>
                    <>Calming anxiety naturally</>
                    <>How to get better sleep</>
                    <>{{default: <>What really works to heal your gut</>, ad: <>Improving gut health</>}[variant]}</>
                    <>Why dental health matters more than you think</>
                    <>{{default: <>Your food and your mood</>, ad: <>The link between food and mood</>}[variant]}</>
                    <>How to master diabetes</>
                    <>Avoiding heart disease</>
                    <>And so much more!</>
                  </ListIcon>
                </div>
                {
                  {
                    default: (
                      <>
                        <p>
                          You’ll get the recorded audio to download, implementation checklists, and articles to learn
                          more — and if you prefer to read, you’ll love getting the transcripts.
                        </p>
                      </>
                    ),
                    ad: (
                      <>
                        <p>
                          Each session includes the recorded audio to download, implementation checklists, and articles
                          to learn more — and if you prefer to read, you’ll love getting the beautiful transcripts of
                          all Action Hours.
                        </p>
                      </>
                    )
                  }[variant]
                }
              </Col>
            </Row>
          </Container>
        </Section>
        <Section id="whole-more-events" color="light-gray" className="text-center">
          <Container>
            <Row>
              <Col>
                <h2 className="section-heading text-uppercase">Weekly Live Events, So You Never Have to Go it Alone</h2>
              </Col>
            </Row>
            <Row className="d-flex align-items-center justify-content-center mb-5">
              <Col sm="10" lg="8">
                <img
                  className="img-border rounded"
                  src="https://cdn.foodrevolution.org/wlc/images/wlc-image-sales-page-monthly-live-events.jpg"
                  alt=""
                  width={1280}
                  height={720}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <p>
                  As a WHOLE Life Club member, you'll have the exclusive opportunity to connect with your fellow members
                  and our dedicated team during our weekly live events. These totally optional (and totally wonderful)
                  events are at the heart of our community, designed to support you on your journey to better health and
                  vitality.
                </p>
                <h5 className="text-center mt-4">So what can you expect from these Weekly Live Events?</h5>
                <p>
                  <b>Culinary Demo Events:</b> Join our brilliant team of chefs, nutritionists and recipe developers as
                  we take ALL the guesswork out of how to prepare delicious, nutritious meals that leave you feeling
                  light but satisfied. Uplevel your culinary skills and explore new recipes that nourish your body and
                  soul.
                </p>
                <p>
                  <b>Culinary and Health Coaching Q&A Calls:</b> Get your burning questions answered by our experienced
                  team of plant-powered coaches, nutrition guides, and wellness mentors. Whether it’s about food,
                  fitness, or lifestyle, our team is here to provide you with personalized support and to answer your
                  questions.
                </p>
                <p>
                  <b>Community Connection Calls:</b> Loneliness and isolation can make sticking to a healthy lifestyle
                  tough. Our Community Connection Calls offer you the chance to build meaningful relationships with
                  like-minded individuals who share your passion for a healthier life. Forge connections, exchange
                  experiences, and feel the power of belonging to a supportive community.
                </p>
                <p>
                  <b>Table Topics Calls:</b> Engage in thoughtful conversations with our team and fellow members as we
                  explore common challenges, share insights, and offer support for living a healthier life. Together,
                  we’ll dive deep into meaningful discussions that strengthen our community and inspire personal growth.
                </p>
                <p>
                  Dietary and lifestyle changes don’t have to be a solitary journey. Our Weekly Live Events are your
                  gateway to finding the support, guidance, and camaraderie you need to thrive.
                </p>
              </Col>
            </Row>
          </Container>
        </Section>
        <Section id="whole-more-4a" color="white">
          <Container>
            <Row>
              <Col>
                <h2 className="section-heading text-uppercase text-center">
                  Guidance from Registered Dietitians, Nutritionists, and Brilliant Health Coaches
                </h2>
              </Col>
            </Row>
            <Row className="d-flex align-items-center justify-content-center">
              <Col sm="10" lg="8" className="mb-4">
                <img
                  className="img-border rounded"
                  src="https://cdn.foodrevolution.org/wlc/images/health-coaches.jpg"
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <h4 className="mt-0 text-center">
                  {variant === 'ad' ? (
                    <>We know how difficult it can be to navigate health questions or new diagnoses alone.</>
                  ) : (
                    <>We know how difficult it can be to navigate health questions or new diagnoses on your own.</>
                  )}
                </h4>
                <h5 className="mt-0 text-center text-400">
                  {variant === 'ad' ? (
                    <>
                      That’s why our WHOLE Life Club coaches are here — to give answers to the most pressing health and
                      nutrition questions!
                    </>
                  ) : (
                    <>
                      That’s why our WHOLE Life Club coaches are here to hold your hand — so you get answers to your
                      most pressing health and nutrition questions!
                    </>
                  )}
                </h5>
                <div className="d-flex justify-content-center">
                  <ListIcon iconColor="purple" icon={faQuestionCircle} className="text-balance mb-0">
                    <>Wondering which foods are good for heart health?</>
                    <>Unclear on the difference between (and optimal ratio for) omega-3s and omega-6s?</>
                    <>
                      Have a loved one with a disturbing diagnosis and wonder what studies say about how food can help?
                    </>
                  </ListIcon>
                </div>
              </Col>
            </Row>
          </Container>
        </Section>
        <SectionImage
          id="whole-more-4b"
          color="white"
          imgUrl="https://cdn.foodrevolution.org/wlc/images/1276385699.jpg"
        >
          <h3 className="section-heading">
            {variant === 'ad' ? (
              <>Anyone who’s ever tried to find answers</>
            ) : (
              <>If you’ve ever tried to find answers</>
            )}
          </h3>
          <p>
            {variant === 'ad' ? (
              <>
                on YouTube or random blogs knows how frustrating it can be to read ten articles that all seem to
                contradict each other or don’t go deep enough.
              </>
            ) : (
              <>
                on YouTube or random blogs, then you know how frustrating it can be to read ten articles that don’t go
                deep enough. Or even worse…
              </>
            )}
          </p>
          {variant != 'ad' && <p>all seem to contradict each other!</p>}
          <p>
            {variant === 'ad' ? (
              <>
                Leery of placing your health in the hands of the peanut gallery in Facebook groups? Can’t ever seem to
                squeeze in all your questions at the doctor’s office? We understand.
              </>
            ) : (
              <>
                If you are leery of placing your health in the hands of the peanut gallery in Facebook groups or can
                never seem to squeeze in all your questions with your doctor, we understand.
              </>
            )}
          </p>
        </SectionImage>

        <Section id="whole-more-4c" color="white">
          <Container>
            <Row>
              <Col>
                <h3 className="section-only-heading text-center">
                  {variant === 'ad' ? (
                    <>
                      That’s why we created a way to get ALL those big and small questions answered by our experienced
                      Community Support team.
                    </>
                  ) : (
                    <>
                      That’s why we created a way for you to get ALL your questions answered by our experienced
                      Community Success team.
                    </>
                  )}
                </h3>
              </Col>
            </Row>
            <Row className="align-items-center justify-content-center my-5">
              <Col xs="4" md="auto" className="px-2">
                <img
                  className="img-border rounded-circle w-100"
                  src="https://cdn.foodrevolution.org/global/team/frn-guest_expert-portrait-square-wilfredobenitez.jpg"
                  alt=""
                  width={1000}
                  height={1000}
                  style={{maxHeight: '200px'}}
                />
              </Col>
              <Col xs="4" md="auto" className="px-2">
                <img
                  className="img-border rounded-circle w-100"
                  src="https://cdn.foodrevolution.org/global/team/frn-guest_expert-portrait-square-roselynnemackay.jpg"
                  alt=""
                  width={1000}
                  height={1000}
                  style={{maxHeight: '200px'}}
                />
              </Col>
              <Col xs="4" md="auto" className="px-2">
                <img
                  className="img-border rounded-circle w-100"
                  src="https://cdn.foodrevolution.org/global/team/nichole-d.jpg"
                  alt=""
                  width={1000}
                  height={1000}
                  style={{maxHeight: '200px'}}
                />
              </Col>
            </Row>
            <Row className="align-items-center justify-content-center text-h5 mb-5">
              <Col xs="12" lg="5" className="mb-4 mb-lg-0">
                <img src="https://cdn.foodrevolution.org/wlc/images/coaches.png" alt="" width={1100} height={616} />
              </Col>
              <Col xs="12" lg="7">
                <p>
                  {variant === 'ad' ? (
                    <>
                      Our team of <b>dietitians, nutritionists, and health coaches</b> has encountered almost everything
                      and will provide feedback and <b>insights based on the latest studies and research.</b>
                    </>
                  ) : (
                    <>
                      Our team includes <b>dietitians, nutritionists, and health coaches</b> who have encountered just
                      about everything. You can count on them to give you feedback and{' '}
                      <b>insights based on the latest studies and research.</b>
                    </>
                  )}
                </p>
              </Col>
            </Row>
            <Row className="align-items-center justify-content-center text-h5">
              <Col xs="12" lg="5" className="order-lg-2 mb-4 mb-lg-0">
                <img src="https://cdn.foodrevolution.org/wlc/images/articles.png" alt="" width={690} height={496} />
              </Col>
              <Col xs="12" lg="7" className="order-lg-1">
                <p>Want to dig deeper?</p>
                <p>
                  {variant === 'ad' ? (
                    <>
                      Our team can direct you to credible <b>articles</b> on <b>just about any health issue</b> that
                      pops up. This kind of help can be a true lifesaver. Our team is ready to offer you{' '}
                      <b>exceptional support.</b>
                    </>
                  ) : (
                    <>
                      Our team can direct you to our credible <b>articles</b> on just about <b>any health issue</b> that
                      pops up. This kind of help can be a true lifesaver. Our team is ready to offer you{' '}
                      <b>exceptional support.</b>
                    </>
                  )}
                </p>
              </Col>
            </Row>
          </Container>
        </Section>
        <Section id="" color="light-gray">
          <Container>
            <Row>
              <Col>
                <h3 className="section-heading">NEW: Wellness Buddy (Beta) — your 24/7 plant-first guide</h3>
                <p>
                  Got 90 seconds and a pantry full of questions? Chat with Wellness Buddy, an AI-powered virtual
                  assistant that's been trained on FRN's articles, Summits, expert interviews, books and courses, for
                  evidence-based meal ideas, fiber-first swaps, grocery lists, and habit tips—any time, on your
                  schedule.
                </p>
                <p>
                  <i>Trained on FRN’s vetted content. Not medical advice.</i>
                </p>
                <h5 className="mt-5 mb-3">What is Wellness Buddy?</h5>
                <p>
                  Your on-demand, plant-first mentor. Ask for 30-gram-fiber day plans, budget grocery lists,
                  kid-friendly swaps, or travel menus—and get practical, stepwise suggestions in seconds.{' '}
                  <i>(Beta; responses may be imperfect and are not medical advice.)</i>
                </p>
              </Col>
            </Row>
          </Container>
        </Section>
        <SectionImage
          id="whole-more-5"
          color="white"
          imgUrl="https://cdn.foodrevolution.org/wlc/images/891187194-square.jpg"
        >
          <h2 className="section-heading text-uppercase">Connect to Community for Even More Uplifting Support</h2>
          {
            {
              default: (
                <>
                  <p>
                    No naysayers here! We know it can be tough to stick to goals if you’re on your own. You deserve a
                    safe space where you are supported by others who are on the same journey as you.
                  </p>
                  <p>
                    In a world where the status quo revolves around toxic food and encourages you to put up with feeling
                    worse each year, we must surround ourselves with others who share our values of healthy food,
                    delicious recipes, and making a better planet for future generations.
                  </p>
                  <p>
                    In this warm and welcoming group, you’ll find encouragement and a space to ask questions. Cheer each
                    other on as we celebrate wins — and the truth is, we celebrate often.
                  </p>
                  <p>You simply have to experience for yourself what a difference this kind of support makes!</p>
                </>
              ),
              ad: (
                <>
                  <p>No naysayers here! We know it can be tough to stick to goals alone.</p>
                  <p>
                    In a world where the status quo revolves around toxic food and encourages us to put up with feeling
                    worse each year, we must surround ourselves with others who share our values of healthy food,
                    delicious recipes, and making a better planet for future generations.
                  </p>
                  <p>
                    Find encouragement in this warm and welcoming group. It’s a space to ask questions and cheer each
                    other on as we celebrate wins — and the truth is, we celebrate often.
                  </p>
                  <p>What a difference this level of support makes!</p>
                </>
              )
            }[variant]
          }
        </SectionImage>
        <Section id="whole-more-5b" color="white" innerClass="pt-0">
          <Container>
            <Row>
              <Col>
                <TestimonialBubble bgColor="light-gray" id="wlcBarbara" />
              </Col>
            </Row>
          </Container>
        </Section>
        <SectionImage
          id="whole-more-6"
          color="light-gray"
          imgPosition="right"
          imgUrl="https://cdn.foodrevolution.org/wlc/images/606196416-square.jpg"
        >
          <h2 className="section-heading text-uppercase">Stay on Top of Cutting-Edge Science</h2>
          <p>We are fortunate to live in the most scientifically advanced period in the history of the world.</p>
          <p>
            However, too many poorly conducted studies wind up misrepresented in articles that amount to little more
            than hyped-up marketing… making it nearly impossible to find accurate information on the topics you care
            about. This is wrong. You deserve the truth.
          </p>
          <p>
            {variant === 'ad' ? (
              <>
                Getting the <i>right</i> information makes all the difference when it comes to topics you care about.
                That’s why we explain the science in language you can understand and provide links to credible research
                so you can be sure it’s grounded in solid evidence.
              </>
            ) : (
              <>
                Getting the <i>right</i> information makes all the difference when it comes to topics like cancer,
                autoimmune disorders, heart disease, thyroid health, weight and metabolism, pain, Alzheimer’s, obesity,
                and more. That’s why we explain the science in language you can understand and provide links to credible
                research so you can be sure it’s grounded in solid evidence.
              </>
            )}
          </p>
          <p>
            New discoveries are made daily — and you can count on us to keep you up-to-date with breaking news from
            reputable sources.
          </p>
        </SectionImage>
        <SectionImage
          id="whole-themes-1"
          color="white"
          imgPosition="left"
          imgUrl="https://cdn.foodrevolution.org/wlc/images/1435001922-square.jpg"
        >
          <h3 className="section-heading">Here’s just some of the recent guidance from WHOLE Life Club</h3>
          <p>
            <i>
              {
                {
                  default: <>You’ll get them all instantly the moment you join!</>,
                  ad: <>Get them all instantly!</>
                }[variant]
              }
            </i>
          </p>
          {
            {
              default: (
                <ListIcon>
                  <>
                    <b>Saving Money with Smart Nutrition</b> — Learn how eating healthy doesn’t have to break the bank —
                    with tips to cut grocery bills, reduce medical costs, and make every bite count.
                  </>
                  <>
                    <b>Foods to Fight Cancer and Help Cells Thrive</b> — See how it’s possible to prevent and even
                    create the conditions to beat cancer.
                  </>
                  <>
                    <b>Preventing Dementia</b> — Employ breakthroughs in nutrition and lifestyle to keep your brain
                    sharp and avoid devastating brain diseases.
                  </>
                  <>
                    <b>Probiotics and Gut Health</b> — Learn more about one of the newest and most promising health
                    discoveries of our time, and get all the breaking updates as they come in.
                  </>
                  <>
                    <b>Hidden Dangers Lurking in Common Foods</b> — Pesticides, glyphosate, food safety, GMOs, and
                    toxins: Know how to protect yourself and your family.
                  </>
                  <>
                    <b>Stopping Heart Disease</b> — Avoid the #1 cause of death by switching to heart-healthy superfoods
                    and adopting positive lifestyle routines you’ll love.
                  </>
                </ListIcon>
              ),
              ad: (
                <ListIcon>
                  <>
                    <b>Saving Money with Smart Nutrition</b> — Learn how eating healthy doesn’t have to break the bank —
                    with tips to cut grocery bills, reduce medical costs, and make every bite count.
                  </>
                  <>
                    <b>Foods to Help Every Cell Thrive</b> — See how it’s possible to fight free radicals and heal
                    misfit cells.
                  </>
                  <>
                    <b>Preventing Cognitive Decline</b> — Employ new breakthroughs in nutrition and lifestyle to keep
                    the brain sharp and memories intact.
                  </>
                  <>
                    <b>Probiotics and Gut Health</b> — Learn more about one of the newest and most promising health
                    discoveries of our time, plus get all the breaking updates as they roll in.
                  </>
                  <>
                    <b>Hidden Dangers Lurking in Common Foods</b> — Get the truth about pesticides, glyphosate, food
                    safety, GMOs, toxins, and more.
                  </>
                  <>
                    <b>Heart Health</b> — Switch to heart-healthy superfoods and adopt positive lifestyle routines.
                  </>
                </ListIcon>
              )
            }[variant]
          }
        </SectionImage>
        <SectionImage
          id="whole-themes-2"
          color="white"
          imgPosition="right"
          imgUrl="https://cdn.foodrevolution.org/wlc/images/1179985039-square.jpg"
        >
          {
            {
              default: (
                <ListIcon>
                  <>
                    <b>Detoxing and Cleansing</b> — Use food to heal your body naturally.
                  </>
                  <>
                    <b>Reversing Chronic Inflammation</b> — Implement the scientifically proven ways to reduce chronic
                    inflammation and help your body heal.
                  </>
                  <>
                    <b>Weight Loss and Food Freedom</b> — Discover how to rewire your brain to eat what’s good for you.
                  </>
                  <>
                    <b>Outdoor and Indoor Gardening</b> — Grow your own fresh fruits, veggies, and herbs for the most
                    delicious flavor and maximum nutrient levels (even in small spaces).
                  </>
                  <>
                    <b>Cultivating Joy and Dialing Down Stress</b> — Learn proven mind-body techniques to become the
                    happiest version of yourself.
                  </>
                  <>
                    <b>Mythbusting</b> — Stop worrying whether you’re doing the right thing when it comes to soy, carbs,
                    protein, fats, and other hotly debated nutrients and foods.
                  </>
                  <>
                    <b>Mental Health</b> — Find out what landmark studies show us about how neuroscience and nutrition
                    can help create balance, resilience, and less suffering.
                  </>
                </ListIcon>
              ),
              ad: (
                <ListIcon>
                  <>
                    <b>Detoxing and Cleansing</b> — Use food to feel incredible again.
                  </>
                  <>
                    <b>Reversing Chronic Inflammation</b> — Implement scientifically proven ways to soothe your cells
                    and tame the fire.
                  </>
                  <>
                    <b>Weight Loss and Food Freedom</b> — Rewire the brain so it’s easier to make great choices using
                    the newest neuroscience.
                  </>
                  <>
                    <b>Outdoor and Indoor Gardening</b> — Grow fresh fruits, veggies, and herbs for the most delicious
                    flavor and maximum nutrient levels (even in small spaces).
                  </>
                  <>
                    <b>Creating Joy and Dealing With Stress</b> — Get true mind-body techniques for happiness.
                  </>
                  <>
                    <b>Mythbusting</b> — Stop wondering about soy, carbs, protein, fats, and other hotly debated foods
                    and nutrients.
                  </>
                  <>
                    <b>Mental Health</b> — Find out what landmark studies show us about how neuroscience and nutrition
                    can help create balance, resilience, and less suffering.
                  </>
                </ListIcon>
              )
            }[variant]
          }
          <h5 className="mt-0">
            {variant === 'ad' ? <>Plus, new topics every week!</> : <>Plus, new topics for you every week!</>}
          </h5>
        </SectionImage>
        <Section id="whole-testimonial-3" color="light-gray">
          <Container>
            <Row>
              <Col>
                <h2 className="section-heading text-center mb-3">What do members have to say?</h2>
                <p className="text-balance text-center text-16 mb-5">
                  <i>
                    After joining WHOLE Life Club, many participants have adopted a whole-food, plant-centered dietary
                    pattern. Their testimonials reflect their unique experiences. Your results may vary.
                  </i>
                </p>
                {['wlcMaureen', 'wlcAlexaP', variant === 'ad' ? 'wlcNerinaSalesAd' : 'wlcNerinaSales', 'wlcKathyW'].map(
                  (key) => (
                    <TestimonialBubble bgColor="white" id={key} key={key} />
                  )
                )}
                <h3 className="mt-4 text-center">
                  No overwhelm.
                  <br />
                  No confusion.
                  <br />
                  No boredom in the kitchen.
                </h3>
                <h3 className="m-0 text-center">
                  {
                    {
                      default: <>Just you making strides, one day at a time. Deliciously!</>,
                      ad: <>Just making strides, one day at a time. Deliciously!</>
                    }[variant]
                  }
                </h3>
              </Col>
            </Row>
          </Container>
        </Section>
        <SalesSection id="whole-cta-1" />
        <Section id="whole-wont-1" color="light-gray">
          <Container>
            <Row className="d-flex justify-content-center">
              <Col lg="10">
                <h2 className="section-only-heading text-center">
                  {
                    {
                      default: (
                        <>
                          What You <i>Won’t</i> Find In WHOLE Life Club
                        </>
                      ),
                      ad: <>What’s NOT inside in WHOLE Life Club:</>
                    }[variant]
                  }
                </h2>
              </Col>
            </Row>
          </Container>
        </Section>
        <SectionImage
          id="whole-wont-2"
          color="light-gray"
          imgPosition="right"
          imgUrl="https://cdn.foodrevolution.org/wlc/images/1351843740.jpg"
        >
          <ListIcon iconColor="red" icon={faTimes} className="mt-0" iconAlignment="center">
            <>
              <h3 className="section-only-heading">
                {variant === 'ad' ? (
                  <>Another book that just sits on the shelf — unread.</>
                ) : (
                  <>A book you buy but don’t read.</>
                )}
              </h3>
            </>
          </ListIcon>
          <p>
            {variant === 'ad' ? (
              <>
                A good book is wonderful, but it can take ten hours — maybe more — to read it cover to cover. Who has
                time to spend weeks learning everything there is to know about a single health topic, even if it will
                majorly impact your life?
              </>
            ) : (
              <>
                A good book is wonderful, but it can take ten hours — maybe more — to read it cover to cover. You might
                not have time to spend weeks learning everything there is to know about a single health topic, even if
                it will majorly impact your life.
              </>
            )}
          </p>
          <p>
            {variant === 'ad' ? (
              <>That’s why we provide short, compact weekly lessons with links for further reading for deep dives.</>
            ) : (
              <>
                That’s why you’ll get short, compact lessons each week, with links for further reading so you can do as
                many deep dives as you wish.
              </>
            )}
          </p>
          <p>
            {variant === 'ad' ? (
              <>
                Then, during Action Hours, get the “greatest hits” from world-class experts on must-have topics that are
                too important to miss.
              </>
            ) : (
              <>
                Then, during Action Hours, you’ll get the “greatest hits” from world-class experts so you can be sure
                you’re not missing vital insights that could change your life overnight.
              </>
            )}
          </p>
        </SectionImage>
        <SectionImage
          id="whole-wont-3"
          color="white"
          imgPosition="left"
          imgUrl="https://cdn.foodrevolution.org/wlc/images/1249628154.jpg"
        >
          <ListIcon iconColor="red" icon={faTimes} className="mt-0" iconAlignment="center">
            <>
              <h3 className="section-only-heading">A bunch of guesswork.</h3>
            </>
          </ListIcon>
          <p>
            Don’t want to go down internet rabbit holes or wait for the next available appointment? We don’t either!
          </p>
          <p>
            {variant === 'ad' ? (
              <>
                That’s why the WHOLE Life Club moderated forum is the place to go for scientifically backed information
                that’s been demonstrated to work in long-running, peer-reviewed studies.
              </>
            ) : (
              <>
                That’s why the WHOLE Life Club moderated forum is the place to go when you need scientifically-backed
                information that’s been demonstrated to work in long-running, peer-reviewed studies.
              </>
            )}
          </p>
          <p>
            {variant === 'ad' ? (
              <>
                Our team of health coaches is standing by to help troubleshoot unique health challenges, answer
                nutrition questions, debunk fads, and point to recipes for reducing stress, mood support, disease
                prevention, and any other important health issue that arises.
              </>
            ) : (
              <>
                Our team of health coaches is standing by to help you troubleshoot your unique health challenges, answer
                nutrition questions, debunk fads, and point you to recipes for reducing stress, mood support, disease
                prevention, and any other important topic on your mind.
              </>
            )}
          </p>
        </SectionImage>
        <SectionImage
          id="whole-wont-4"
          color="light-gray"
          imgPosition="right"
          imgUrl="https://cdn.foodrevolution.org/wlc/images/1284549946.jpg"
        >
          <ListIcon iconColor="red" icon={faTimes} className="mt-0" iconAlignment="center">
            <>
              <h3 className="section-only-heading">Another Facebook group.</h3>
            </>
          </ListIcon>
          <p>Not everyone loves Facebook.</p>
          {variant === 'ad' ? (
            <p>
              So, we created a warm space to connect with WHOLE Life Club registered dietitians, certified health
              coaches, and new friends who are on the same path as you and who share your enthusiasm.
            </p>
          ) : (
            <>
              <p>
                So, we created a warm space where you can focus on what you want when you want (without having your
                eyeballs sold to advertisers).
              </p>
              <p>
                Connect with WHOLE Life Club registered dietitians, certified health coaches, and new friends who share
                your enthusiasm and are on the same path as you.
              </p>
            </>
          )}
          <p>
            {variant === 'ad' ? (
              <>Conversations are sorted by category, making it easy to ask questions and find answers quickly.</>
            ) : (
              <>Conversations are sorted by category, making it easy to ask your questions and find answers.</>
            )}
          </p>
        </SectionImage>
        <SectionImage
          id="whole-wont-5"
          color="white"
          imgPosition="left"
          imgUrl="https://cdn.foodrevolution.org/wlc/images/1323957894.jpg"
        >
          <ListIcon iconColor="red" icon={faTimes} className="mt-0" iconAlignment="center">
            <>
              <h3 className="section-only-heading">A solo adventure.</h3>
            </>
          </ListIcon>
          <p>
            {variant === 'ad' ? (
              <>
                Have you ever tried to make a New Year’s resolution without someone else who’s committed to the same
                journey? Most might make it a week or two before slipping back to old ways.
              </>
            ) : (
              <>
                Have you ever tried to make a New Year’s resolution without someone else who’s committed to the same
                journey? If you try really hard, you might make it a while before you slip back to your old ways, but it
                can be hard to make it stick on your own.
              </>
            )}
          </p>
          <p>
            {variant === 'ad' ? (
              <>
                The WHOLE Life Club community is a life raft. Find cheerleaders, brainstorm ideas, meet accountability
                partners, and stay motivated. THIS is how to create healthy habits that last a lifetime.
              </>
            ) : (
              <>
                The WHOLE Life Club community is your life raft towards your own best self.. Post whenever you need a
                cheerleader or help brainstorming ideas. Find accountability partners and stay motivated. THIS is how
                you create healthy habits that you enjoy and adopt for life.
              </>
            )}
          </p>
          <p>
            {variant === 'ad' ? (
              <>
                Plus, bring your loved ones along for the ride by making new recipes with them, watching the Action
                Hours together, and inspiring them to take great care of themselves, too.
              </>
            ) : (
              <>
                Plus, you’ll be able to bring your loved ones along for the ride as you enjoy new recipes, watch the
                Action Hours together, and inspire them to take great care of themselves, too.
              </>
            )}
          </p>
        </SectionImage>
        <SectionImage
          id="whole-wont-6"
          color="light-gray"
          imgPosition="right"
          imgUrl="https://cdn.foodrevolution.org/wlc/images/186546449.jpg"
        >
          <ListIcon iconColor="red" icon={faTimes} className="mt-0" iconAlignment="center">
            <>
              <h3 className="section-only-heading">Another night of uninspired dinners.</h3>
            </>
          </ListIcon>
          <p>There’s so much more to eating healthy than frozen bean burritos and boring salads.</p>
          <p>
            {variant === 'ad' ? (
              <>
                Enjoy hundreds of mouthwatering recipes that can be whipped up quickly and inexpensively. These include
                healthy, veggie-forward versions of comfort classics and new dishes that may become go-to favorites.
              </>
            ) : (
              <>
                You’ll discover mouthwatering recipes you can whip up quickly and inexpensively, including healthy,
                veggie-forward versions of comfort classics, as well as new dishes that may become your go-to favorites.
              </>
            )}
          </p>
          <p>
            {variant === 'ad' ? (
              <>Go ahead, tantalize your taste buds — even picky eaters will want these recipes on heavy rotation!</>
            ) : (
              <>
                Go ahead, tantalize your taste buds — even the pickiest eater will want these recipes on heavy rotation!
              </>
            )}
          </p>
        </SectionImage>
        <SectionImage
          id="whole-wont-7"
          color="white"
          imgPosition="left"
          imgUrl="https://cdn.foodrevolution.org/wlc/images/976657866.jpg"
        >
          <ListIcon iconColor="red" icon={faTimes} className="mt-0" iconAlignment="center">
            <>
              <h3 className="section-only-heading">
                {
                  {
                    default: <>A drill sergeant telling you what you’re “supposed” to do.</>,
                    ad: <>A drill sergeant.</>
                  }[variant]
                }
              </h3>
            </>
          </ListIcon>
          {variant === 'ad' ? (
            <p>Life’s more fun with positivity and gentle encouragement.</p>
          ) : (
            <>
              <p>
                When you have people cheering you on, as well as health tips and health hacks spoonfed to you, it all
                adds up!
              </p>
              <p>You’ll be able to do a little bit better every day and truly enjoy the process.</p>
            </>
          )}
          <p>
            {variant === 'ad' ? (
              <>
                The entire focus of WHOLE Life Club is to get results, do a little bit better every day, and truly enjoy
                the process.
              </>
            ) : (
              <>
                The entire focus of WHOLE Life Club is to help open your eyes, get results, and have fun so you keep
                coming back.
              </>
            )}
          </p>
          <p>
            {variant === 'ad' ? (
              <>
                Ocean’s weekly videos are filled with encouragement, action tips, and health hacks. He breaks down
                complicated subjects so they’re easy to understand, making healthy choices second nature.
              </>
            ) : (
              <>
                You’ll also be hearing from Ocean regularly, with videos, action tips, and potent resources — and trust
                us; these are emails you’ll actually want to read!
              </>
            )}
          </p>
        </SectionImage>
        <Section id="whole-testimonial-4" color="black" innerClass="text-white">
          <Container>
            <Row>
              <Col>
                <TestimonialBubble id="wlcLaurieC" bgColor="white" textColor="black" />
              </Col>
            </Row>
          </Container>
        </Section>
        <SectionImage
          id="whole-motivation"
          color="white"
          imgPosition="right"
          imgUrl="https://cdn.foodrevolution.org/wlc/images/couple-happy-bowls-square.jpg"
          className="text-center text-lg-left"
        >
          <h2 className="section-heading mb-3">
            {
              {
                default: <>Motivation to Live Your Healthiest Life</>,
                ad: <>Motivation to Live a Healthy Life</>
              }[variant]
            }
          </h2>
          <h4 className="text-400 mt-0">
            {
              {
                default: <>WHOLE Life Club doesn’t just give you information…</>,
                ad: <>WHOLE Life Club doesn’t just offer information…</>
              }[variant]
            }
          </h4>
          <h4 className="text-400 mt-0">{variant === 'ad' ? <>It creates power.</> : <>It gives you power.</>}</h4>
          <h4 className="text-400 my-4">
            <i>
              One meal,
              <br />
              One discovery,
              <br />
              One conversation,
              <br />
              One new friend…
            </i>
          </h4>
          <h4 className="text-400 mt-0">At a time.</h4>
        </SectionImage>
        <SectionImage
          id="whole-pressure"
          color="light-gray"
          borderColor="light-gray"
          className="section-angle-desktop-none"
          imgUrl="https://cdn.foodrevolution.org/wlc/images/woman-cellphone-square.jpg"
        >
          <Container>
            <Row>
              <Col>
                <h2 className="section-heading">A Few Words About Social Pressure.</h2>
                <p>
                  <i>Ever given in to peer pressure?</i> It’s quite common.
                </p>
                <p>
                  {variant === 'ad' ? (
                    <>It’s harder to have a healthy lifestyle when friends and family aren’t fully on board.</>
                  ) : (
                    <>It’s harder to have a healthy lifestyle when your friends and family aren’t fully on board.</>
                  )}
                </p>
                <p>
                  {
                    {
                      default: (
                        <>
                          This stress becomes even more powerful when you have an underlying illness or condition that
                          no one else seems to understand.
                        </>
                      ),
                      ad: (
                        <>
                          This stress becomes even more powerful when there’s an underlying illness or condition that no
                          one else seems to understand.
                        </>
                      )
                    }[variant]
                  }
                </p>
                {variant === 'ad' ? (
                  <>
                    <p>
                      When people around you are bonding over unhealthy foods, it’s common to feel sad, disconnected, or
                      disappointed.
                    </p>
                  </>
                ) : (
                  <>
                    <p>
                      When people around you are bonding over unhealthy foods, you may feel sad, disconnected, or
                      disappointed.
                    </p>
                    <p>They might also express discomfort that you’re not joining in.</p>
                  </>
                )}
                <p>
                  {variant === 'ad' ? (
                    <>Worst of all, it’s tempting to compromise to fit in.</>
                  ) : (
                    <>
                      Worst of all, you might compromise your food values to fit in or for fear of hurting someone’s
                      feelings — and ultimately, you might regret it.
                    </>
                  )}
                </p>
                {variant != 'ad' && (
                  <p className="mb-0">
                    Unless you have ample support, your social life can pull you in unhealthy directions.
                  </p>
                )}
              </Col>
            </Row>
          </Container>
        </SectionImage>
        <SectionImage
          id="whole-pressure-2"
          color="light-gray"
          imgPosition="right"
          className="section-image-offset-top-desktop"
          imgUrl="https://cdn.foodrevolution.org/wlc/images/woman-yellow-sweater.jpg"
        >
          <h2 className="section-heading">
            The Solution to Yo-Yo Health Commitments? A Welcoming, Supportive Community
          </h2>
          <p>
            {variant === 'ad' ? (
              <>
                Become part of a moderated, thoughtful community of health-minded people ready to overcome whatever
                challenges arise — now or in the future.
              </>
            ) : (
              <>
                The instant you join, you’ll gain access to a moderated, thoughtful community of health-minded people
                ready to help you overcome whatever challenges you may face — now or in the future.
              </>
            )}
          </p>
          <p>
            {variant === 'ad' ? (
              <>This is a solid community, ready to celebrate victories and milestones...</>
            ) : (
              <>This is your community, ready to cheer you on and celebrate your victories and milestones...</>
            )}
          </p>
          <h3 className="mt-4">And to motivate you from “I know I should” to “I’m doing it!”</h3>
        </SectionImage>
        <SalesSection
          id="whole-cta-2"
          headline={
            <Row className="text-center text-white mb-5">
              <Col>
                <h2 className={classNames(`section-heading`, `mb-0`)}>
                  Make Sticking to a Healthy Lifestyle Finally Happen for You!
                </h2>
              </Col>
            </Row>
          }
        />
        <Section id="whole-why" color="white">
          <Container>
            <Row className="d-flex justify-content-center">
              <Col>
                <h2 className="section-heading text-center mb-5">Why Join WHOLE Life Club Today?</h2>
                <ImgCard imgColLg="4" textColLg="8" img="https://cdn.foodrevolution.org/wlc/images/536034047.jpg">
                  <h4 className="section-heading">Save money.</h4>
                  <p>
                    {variant === 'ad' ? (
                      <>
                        Get inspired to cook more meals at home. With just one less takeout meal a month, membership
                        pays for itself. And with fewer trips to the doctor and pharmacy aisle in the future, that could
                        add up to <i>thousands</i> of dollars saved.
                      </>
                    ) : (
                      <>
                        You’ll be inspired to cook more meals at home. With just one less takeout meal a month, your
                        membership will pay for itself. With fewer trips to the doctor and pharmacy aisle in the future,
                        you could save <i>thousands.</i>
                      </>
                    )}
                  </p>
                </ImgCard>
                <ImgCard imgColLg="4" textColLg="8" img="https://cdn.foodrevolution.org/wlc/images/607618482.jpg">
                  <h4 className="section-heading">Save time.</h4>
                  <p>
                    Fast answers, less searching. Stuck on dinner, travel eating, or fiber math? Ask Wellness Buddy for
                    a 10-minute plan and get back to living.
                  </p>
                  <p>
                    {variant === 'ad' ? (
                      <>
                        Plus, all WHOLE Life Club recipes can be adapted for gluten-, sugar-, oil-, and/or salt-free
                        diets, so it’s a cinch to find meals to accommodate everyone.
                      </>
                    ) : (
                      <>
                        Plus, all recipes can be adapted for gluten-, sugar-, oil-, and/or salt-free diets, so you won’t
                        waste time hunting for meals you can eat.
                      </>
                    )}
                  </p>
                </ImgCard>
                <ImgCard imgColLg="4" textColLg="8" img="https://cdn.foodrevolution.org/wlc/images/876656860.jpg">
                  <h4 className="section-heading">
                    {{default: <>Save your health.</>, ad: <>Save health.</>}[variant]}
                  </h4>
                  <p>
                    Don’t miss out on critical health topics that every single person needs to understand right now.
                  </p>
                  <p>
                    {variant === 'ad' ? (
                      <>
                        WHOLE Life Club is like a total health encyclopedia, providing a complete health education
                        that’s otherwise easy to miss.
                      </>
                    ) : (
                      <>
                        WHOLE Life Club is like a total health encyclopedia, giving you the resources you need when you
                        need them.
                      </>
                    )}
                  </p>
                </ImgCard>
                <ImgCard imgColLg="4" textColLg="8" img="https://cdn.foodrevolution.org/wlc/images/1247928374.jpg">
                  <h4 className="section-heading">
                    {{default: <>Save yourself the hassle.</>, ad: <>Save the hassle.</>}[variant]}
                  </h4>
                  <p>
                    {variant === 'ad' ? (
                      <>
                        From seasoned chef to total beginner, WHOLE Life Club divulges tips to make cooking and menu
                        planning fast and fun.
                      </>
                    ) : (
                      <>
                        Whether you’re a seasoned chef or a beginner, you’ll get tips and tricks to help you make
                        cooking and menu planning fun.
                      </>
                    )}
                  </p>
                </ImgCard>
                <ImgCard imgColLg="4" textColLg="8" img="https://cdn.foodrevolution.org/wlc/images/1080244822.jpg">
                  <h4 className="section-heading">Save the planet.</h4>
                  <p>
                    {variant === 'ad' ? (
                      <>
                        Eating more veggies is one of the best things we can do to save soil and water, reduce
                        pesticides, fight climate change, and preserve the beautiful Earth we call home.
                      </>
                    ) : (
                      <>
                        Putting more veggies on your plate is one of the best things you can do to save soil and water,
                        reduce pesticides, fight climate change, and preserve the beautiful Earth we call home.
                      </>
                    )}
                  </p>
                </ImgCard>
                <ImgCard imgColLg="4" textColLg="8" img="https://cdn.foodrevolution.org/wlc/images/1299818810.jpg">
                  <h4 className="section-heading">Save animals from harm.</h4>
                  <p>
                    {
                      {
                        default: (
                          <>
                            Feel good knowing that your food choices are as good for you as they are for other
                            creatures, big and small. We’re all in this together!
                          </>
                        ),
                        ad: (
                          <>
                            Feel good knowing that our food choices are as good for us as they are for other creatures,
                            big and small. We’re all in this together!
                          </>
                        )
                      }[variant]
                    }
                  </p>
                </ImgCard>
              </Col>
            </Row>
          </Container>
        </Section>
        <Section id="whole-guarantee" color="light-gray">
          <Container>
            <Row>
              <Col>
                <GuaranteeBox title={<>Our 60-Day, Money-Back, Risk-Free Guarantee</>}>
                  <p>Take up to 60 days to see what WHOLE Life Club is all about.</p>
                  {variant != 'ad' && (
                    <>
                      <p className="mb-4">We guarantee that you and your family will:</p>
                      <IconList>
                        <>
                          Have the tools and information to improve your health right away, helping you eat better and
                          enjoy more vitality.
                        </>
                        <>Feel empowered knowing the truth about the foods you eat.</>
                        <>Make meaningful progress towards YOUR healthy eating goals.</>
                      </IconList>
                    </>
                  )}
                  <p>
                    {variant === 'ad' ? (
                      <>
                        Your satisfaction is important to us! If you are not 100% thrilled, request a prompt, total
                        refund within 60 days. Email us at{' '}
                        <a className="link-black text-underline" href="mailto:support@foodrevolution.org">
                          support@foodrevolution.org
                        </a>
                        , phone us at{' '}
                        <a className="link-black text-underline" href="tel:8318244779">
                          (831) 824-4779
                        </a>
                        , or reply to any email we send with your request.
                      </>
                    ) : (
                      <>
                        Your satisfaction is important to us! If, for some reason, you aren’t 100% thrilled with your
                        purchase, you have 60 days to request a prompt, total refund. Email us at{' '}
                        <a className="text-underline" href="mailto:support@foodrevolution.org">
                          support@foodrevolution.org
                        </a>{' '}
                        or phone us at{' '}
                        <a className="text-underline" href="tel:8318244779">
                          (831) 824-4779
                        </a>
                        . You can also reply to any email we send you with your request.
                      </>
                    )}
                  </p>
                  <p className="mb-0">
                    {variant === 'ad' ? (
                      <>Keep everything already downloaded. That’s why we call our guarantee risk-free!</>
                    ) : (
                      <>
                        If you request a refund for any reason, you can keep everything you've already downloaded.
                        That’s why we call our guarantee risk-free!
                      </>
                    )}
                  </p>
                </GuaranteeBox>
              </Col>
            </Row>
          </Container>
        </Section>
        <SalesSection id="whole-cta-3" className="section-angle-last" angle="none" trees />
      </div>
      <Footer />
      {noExitIntent ? null : <>{/* modal */}</>}
    </div>
  );
}
